// AWS accepts no partner updates in these review statuses.
const AWS_IMMUTABLE_REVIEW_STATUSES = new Set(["Submitted", "In review", "Rejected"]);

const ACTION_REQUIRED_EDITABLE_FIELDS = new Set([
  "city",
  "country",
  "postalCode",
  "state",
  "streetAddress",
  "website",
  "targetCloseDate",
  "estimatedAwsMonthlyReccuringRevenue",
  "expectedCustomerSpendCurrencyCode",
  "expectedCustomerSpendFrequency",
  "expectedCustomerSpendTargetCompany",
  "customerBusinessProblem",
  "partnerCrmUniqueId",
  "partnerCrmUnique",
  "address",
]);

export const AWS_APPROVED_READONLY_FIELDS = [
  "country",
  "postalCode",
  "industryVertical",
  "website",
  "customerBusinessProblem",
  "partnerCrmUniqueId",
  "partnerCrmUnique",
  "partnerProjectTitle",
  "dealName",
];

export const AWS_SUBMITTED_IN_REVIEW_READONLY_FIELDS = [
  "city",
  "country",
  "postalCode",
  "state",
  "streetAddress",
  "website",
  "targetCloseDate",
  "estimatedAwsMonthlyReccuringRevenue",
  "expectedCustomerSpendCurrencyCode",
  "expectedCustomerSpendFrequency",
  "expectedCustomerSpendTargetCompany",
  "customerBusinessProblem",
  "partnerCrmUniqueId",
  "partnerCrmUnique",
  "customerCompanyName",
  "customerDuns",
  "awsAccountId",
  "industryVertical",
  "industryOther",
  "partnerProjectTitle",
  "opportunityType",
  "nextSteps",
  "closedLostReason",
  "doesOppBelongToNatSec",
  "awsProducts",
  "awsProductIds",
  "opportunityCustomer",
  "primaryNeedsFromAws",
  "dealName",
  "address",
];

export const AWS_ACTION_REQUIRED_READONLY_FIELDS =
  AWS_SUBMITTED_IN_REVIEW_READONLY_FIELDS.filter(
    (field) => !ACTION_REQUIRED_EDITABLE_FIELDS.has(field)
  );

const normalizeReviewStatus = (reviewStatus: string | undefined): string =>
  typeof reviewStatus === "string" ? reviewStatus.trim() : "";

export const getReadonlyFieldsForAwsReviewStatus = (
  reviewStatus: string | undefined
): string[] => {
  const status = normalizeReviewStatus(reviewStatus);
  if (AWS_IMMUTABLE_REVIEW_STATUSES.has(status)) {
    return AWS_SUBMITTED_IN_REVIEW_READONLY_FIELDS;
  }
  if (status === "Action Required") {
    return AWS_ACTION_REQUIRED_READONLY_FIELDS;
  }
  if (status === "Approved") {
    return AWS_APPROVED_READONLY_FIELDS;
  }
  return [];
};

export const isFieldEditableForAwsReviewStatus = (
  field: string,
  reviewStatus: string | undefined
): boolean => {
  const status = normalizeReviewStatus(reviewStatus);
  if (AWS_IMMUTABLE_REVIEW_STATUSES.has(status)) return false;
  if (status === "Action Required") return ACTION_REQUIRED_EDITABLE_FIELDS.has(field);
  if (status === "Approved") return !AWS_APPROVED_READONLY_FIELDS.includes(field);
  return true;
};

// Status of the record open in the gruim edit view, set by the fetch normalizer.
let activeAwsReviewStatus = "";

export const setActiveAwsReviewStatus = (reviewStatus: string | undefined): void => {
  activeAwsReviewStatus = normalizeReviewStatus(reviewStatus);
};

export const getActiveAwsReviewStatus = (): string => activeAwsReviewStatus;

// gruim only calls `.includes()` on `readonly-fields`, so it can be dynamic.
export const createStatusAwareReadonlyFields = (
  baseFields: string[]
): { includes: (field: string) => boolean } => ({
  includes: (field: string): boolean =>
    baseFields.includes(field) ||
    getReadonlyFieldsForAwsReviewStatus(activeAwsReviewStatus).includes(field),
});

